import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';

import {AccessKey, UserRight, UserRightType, UserRole} from '../util/interfaces';

import {getAccountUsers, revokeAccountUser, updateAccountUser} from '../util/requestSender';

import IDate from '@dvrd/idate';

import {deepClone} from '../util/utils';

interface Props extends BaseProps {
    user_id?: string;
    account_id?: string;
    rights?: Array<UserRight>;
    user_role?: UserRole;
    email?: string;
    firstname?: string;
    lastname?: string;
    last_login?: string | null;
}

export interface AccountUserFields extends BaseFields {
    user_id: string;
    account_id: string;
    rights: Array<UserRight>;
    user_role: UserRole;
    email: string;
    firstname: string;
    lastname: string;
    last_login: string | null;
}

const fields: AccountUserFields = {
    id: '',
    created_at: '',
    updated_at: '',
    user_id: '',
    account_id: '',
    rights: [],
    user_role: UserRole.ACCOUNTANT,
    email: '',
    firstname: '',
    lastname: '',
    last_login: null
};

const fieldsDefinition: {[key in keyof AccountUserFields]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    user_id: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    email: createFieldDef(ModelFieldType.STRING),
    firstname: createFieldDef(ModelFieldType.STRING),
    lastname: createFieldDef(ModelFieldType.STRING),
    rights: createFieldDef(ModelFieldType.ARRAY),
    user_role: createFieldDef(ModelFieldType.STRING, UserRole.ACCOUNTANT),
    last_login: createFieldDef(ModelFieldType.STRING, null, true)
};

const ignoreProps: Array<keyof Props> = [];

export default class AccountUser extends BaseModel<Props, AccountUserFields> {
    static getAll = (
        accountId: string,
        search: string | null,
        page: number,
        limit: number,
        callback: ModelResponse<Array<AccountUser>>
    ) => {
        return getAccountUsers({
            data: {account_id: accountId, search, page, limit},
            callback: (data: ResponseData) => {
                const accountUsers: Array<AccountUser> = data.success
                    ? data.account_users.map((props: any) => new AccountUser(props))
                    : [];
                callback(accountUsers, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get userId(): string {
        return this.fields.user_id;
    }

    set userId(user_id: string) {
        this.fields.user_id = user_id;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get rights(): Array<UserRight> {
        return this.fields.rights;
    }

    set rights(rights: Array<UserRight>) {
        this.fields.rights = rights;
    }

    get userRole(): UserRole {
        return this.fields.user_role;
    }

    set userRole(user_role: UserRole) {
        this.fields.user_role = user_role;
    }

    get email(): string {
        return this.fields.email;
    }

    set email(email: string) {
        this.fields.email = email;
    }

    get firstname(): string {
        return this.fields.firstname;
    }

    set firstname(firstname: string) {
        this.fields.firstname = firstname;
    }

    get lastname(): string {
        return this.fields.lastname;
    }

    set lastname(lastname: string) {
        this.fields.lastname = lastname;
    }

    get fullName(): string {
        let fullName = this.fields.firstname;
        if (this.fields.lastname) fullName += ` ${this.fields.lastname}`;
        return fullName;
    }

    get lastLogin(): string | null {
        return this.fields.last_login;
    }

    get lastLoginDate(): IDate | null {
        if (this.lastLogin) return new IDate(this.lastLogin);
        return null;
    }

    isOwner = (): boolean => {
        return this.fields.user_role === UserRole.OWNER;
    };

    findRight = (key: string): UserRight | null => {
        return this.rights.find((right: UserRight) => right.key === key) ?? null;
    };

    setRight = (right: UserRight): AccountUser => {
        const rights = this.rights.slice();
        for (let i = 0; i < rights.length; i++) {
            if (rights[i].key === right.key) {
                rights.splice(i, 1, right);
                this.rights = rights;
                return this;
            }
        }
        // Not found, add new right
        this.rights = this.rights.concat(right);
        return this;
    };

    setRightValue = (key: AccessKey, value: boolean) => {
        const right: UserRight = this.findRight(key) ?? {key, allow: value, right_type: UserRightType.ACTION};
        return this.setRight(right);
    };

    clone = (deep: boolean = true): AccountUser => {
        let props = this.fields;
        if (deep) props = deepClone(props);
        return new AccountUser(props);
    };

    update = (callback: ModelResponse<AccountUser>) => {
        updateAccountUser({
            data: {
                account_id: this.accountId,
                user_id: this.userId,
                user_role: this.userRole,
                rights: this.rights
            },
            callback: (data: ResponseData) => {
                if (data.success) this.build(data.account_user);
                callback(this, data.success, data);
            }
        });
    };

    revoke = (callback: ModelResponse<AccountUser>) => {
        revokeAccountUser({
            account_id: this.accountId,
            user_id: this.userId,
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}
